"use client"

import { useRef, useState, useEffect } from "react"
import NextImage from "next/image"
import { motion, useInView, AnimatePresence } from "framer-motion"
import { Star, ChevronLeft, ChevronRight } from "lucide-react"
import Link from "next/link"

const testimonials = [
  {
    name: "Sarah Johnson",
    position: "CFO, Global Finance Corp",
    image: "/placeholder.svg?height=100&width=100",
    quote:
      "Annual ESG transformed our annual report from a dry financial document into a compelling narrative that our stakeholders love. The design quality and attention to detail exceeded our expectations.",
    rating: 5,
  },
  {
    name: "Bijan Mishra",
    position: "VP - Energy Reliance power",
    image: "/placeholder.svg?height=100&width=100",
    quote:
      "Working with Annual ESG has been a game-changer for our investor communications. Their team understood our vision perfectly and delivered a report that effectively communicates our complex business model.",
    rating: 5,
  },
  {
    name: "Priya Sharma",
    position: "Sustainability Director, EcoSolutions",
    image: "/placeholder.svg?height=100&width=100",
    quote:
      "Our sustainability report needed to balance data with storytelling, and Annual ESG delivered exactly that. The infographics and visual elements they created made our environmental impact clear and engaging.",
    rating: 5,
  },
  {
    name: "Robert Williams",
    position: "CEO, Williams Enterprises",
    image: "/placeholder.svg?height=100&width=100",
    quote:
      "The team at Annual ESG are true professionals. They took our complex financial data and transformed it into a visually stunning report that our board and investors praised highly.",
    rating: 5,
  },
  {
    name: "Jennifer Lee",
    position: "Marketing Director, Global Retail",
    image: "/placeholder.svg?height=100&width=100",
    quote:
      "The annual report designed by Annual ESG received overwhelmingly positive feedback from our shareholders. Their ability to translate our brand identity into the report design was impressive.",
    rating: 5,
  },
  {
    name: "David Patel",
    position: "Finance Manager, Tech Solutions",
    image: "/placeholder.svg?height=100&width=100",
    quote:
      "Annual ESG's attention to detail and commitment to excellence made our annual report stand out. They were responsive, professional, and delivered beyond our expectations.",
    rating: 5,
  },
]

export default function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [direction, setDirection] = useState(0) // -1 for left, 1 for right
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, { once: false, amount: 0.2, margin: "-100px" })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  }

  // Function to get visible testimonials (3 at a time)
  const getVisibleTestimonials = () => {
    const result = []
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % testimonials.length
      result.push(testimonials[index])
    }
    return result
  }

  const nextSlide = () => {
    setDirection(1)
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length)
  }

  const prevSlide = () => {
    setDirection(-1)
    setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length)
  }

  // Auto-scroll functionality
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide()
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  // Animation variants for slide transitions
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: "easeIn",
      },
    }),
  }

  return (
    <section
      ref={sectionRef}
      className="md:py-20 py-16 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
      id="testimonials"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-dots-pattern opacity-5"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
          className="text-center mb-16"
        >
          {/* <motion.span
            variants={itemVariants}
            className="inline-block mb-4 px-4 py-1 rounded-full bg-gray-200 text-gray-800 font-medium text-sm"
          >
            Testimonials
          </motion.span> */}

          <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Testimonials
          </motion.h2>

          <motion.p variants={itemVariants} className="text-lg text-gray-600 max-w-3xl mx-auto">
            Don't just take our word for it. Here's what our clients have to say about their experience working with Annual ESG
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
          className="relative"
        >
          {/* Desktop View - 3 cards */}
          <div className="hidden md:block relative overflowhidden">
            <AnimatePresence custom={direction} mode="popLayout">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="grid grid-cols-3 gap-6"
              >
                {getVisibleTestimonials().map((testimonial, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 h-full flex flex-col"
                  >
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-gray-800 fill-gray-800" />
                      ))}
                    </div>

                    <p className="text-gray-700 italic mb-6 flex-grow text-sm">{testimonial.quote}</p>

                    <div className="flex items-center mt-auto pt-4 border-t border-gray-100">
                      {/* <div className="relative w-10 h-10 rounded-full overflow-hidden mr-3">
                        <NextImage src="https://png.pngtree.com/png-clipart/20231019/original/pngtree-user-profile-avatar-png-image_13369991.png" alt={testimonial.name} fill className="object-cover" />
                      </div>  */}
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm">{testimonial.name}</h4>
                        <p className="text-gray-600 text-xs">{testimonial.position}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Mobile View - 1 card */}
          <div className="md:hidden relative overflow-hidden">
            <AnimatePresence custom={direction} mode="popLayout">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                  <div className="flex mb-4">
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-gray-800 fill-gray-800" />
                    ))}
                  </div>

                  <p className="text-gray-700 italic mb-6">{testimonials[currentIndex].quote}</p>

                  <div className="flex items-center mt-auto pt-4 border-t border-gray-100">
                    <div className="relative w-12 h-12 rounded-full overflow-hidden mr-4">
                      <NextImage
                        src={testimonials[currentIndex].image}
                        alt={testimonials[currentIndex].name}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{testimonials[currentIndex].name}</h4>
                      <p className="text-gray-600 text-sm">{testimonials[currentIndex].position}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-center mt-10 gap-4">
            <button
              onClick={prevSlide}
              className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-800 hover:bg-gray-800 hover:text-white transition-colors shadow-md"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextSlide}
              className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-800 hover:bg-gray-800 hover:text-white transition-colors shadow-md"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Pagination Dots */}
          <div className="flex justify-center mt-6 gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1)
                  setCurrentIndex(index)
                }}
                className={`w-2 h-2 rounded-full transition-all ${currentIndex === index ? "bg-gray-800 w-6" : "bg-gray-300"
                  }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
